import React, { useState, useEffect } from 'react';
import {
    Table,
    Input,
    Button,
    Typography,
    Card,
    Space,
    Tooltip,
    Spin,
    App
} from 'antd';
import {
    UserOutlined,
    SearchOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {userService} from '../services/api';
import { usePageTitle } from '../context/PageTitleContext';

const { Title } = Typography;

const UsersList = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const navigate = useNavigate();
    const { message } = App.useApp(); // Используем хук для доступа к message API

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);

    useEffect(() => {
        // Устанавливаем заголовок для страницы
        setPageTitle('Пользователи');

        setRightContent(
            <Button
                type="primary"
                size="large"
                onClick={() => navigate('/users/create')}
            >
                Создать пользователя
            </Button>
        );

        // Очистка при размонтировании
        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent, navigate]);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (users.length > 0) {
            filterUsers(searchText);
        }
    }, [searchText, users]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await userService.getAllUsersList();

            // Изменено: правильная проверка ответа API
            if (response && response.status === 'success') {
                setUsers(response.data);
                setFilteredUsers(response.data);
            } else {
                message.error(response?.message || 'Не удалось загрузить список пользователей');
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Ошибка при загрузке пользователей');
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterUsers = (value) => {
        const searchValue = value.toLowerCase();
        const filtered = users.filter(user =>
            user.username.toLowerCase().includes(searchValue) ||
            (user.first_name && user.first_name.toLowerCase().includes(searchValue)) ||
            (user.last_name && user.last_name.toLowerCase().includes(searchValue)) ||
            user.email.toLowerCase().includes(searchValue)
        );
        setFilteredUsers(filtered);
    };

    const handleSearch = (e) => {
        setSearchText(e.target.value);
    };

    const viewUserDetails = (userId) => {
        navigate(`/users/${userId}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleDateString('ru-RU');
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
        },
        {
            title: 'Логин',
            dataIndex: 'username',
            key: 'username',
            sorter: (a, b) => a.username.localeCompare(b.username),
            render: (text, record) => (
                <Typography.Link onClick={() => viewUserDetails(record.id)}>
                    {text}
                </Typography.Link>
            ),
        },
        {
            title: 'Имя',
            dataIndex: 'first_name',
            key: 'first_name',
            sorter: (a, b) => (a.first_name || '').localeCompare(b.first_name || ''),
            render: (text) => text || 'Не указано',
        },
        {
            title: 'Фамилия',
            dataIndex: 'last_name',
            key: 'last_name',
            sorter: (a, b) => (a.last_name || '').localeCompare(b.last_name || ''),
            render: (text) => text || 'Не указано',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => a.email.localeCompare(b.email),
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            filters: [
                { text: 'Admin', value: 'admin' },
                { text: 'User', value: 'user' },
            ],
            onFilter: (value, record) => record.role === value,
            render: (text) => text.charAt(0).toUpperCase() + text.slice(1),
        },
        {
            title: 'Статус',
            dataIndex: 'is_active',
            key: 'is_active',
            filters: [
                { text: 'Активен', value: 1 },
                { text: 'Неактивен', value: 0 },
            ],
            onFilter: (value, record) => record.is_active === value,
            render: (status) => (
                <span style={{ color: status ? 'green' : 'red' }}>
                    {status ? 'Активен' : 'Неактивен'}
                </span>
            ),
        },
        {
            title: 'Дата регистрации',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => formatDate(text),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
    ];

    return (
        <>
            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Space>
                        <Input
                            placeholder="Поиск по логину, имени, фамилии, email..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={handleSearch}
                            style={{ width: 350 }}
                            allowClear
                        />
                    </Space>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={filteredUsers}
                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                        }}
                        size="middle"
                        bordered
                    />
                )}
            </Card>
        </>
    );
};

export default UsersList;